import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import { KVSLoader } from '../../../components/loaders';
import img1 from '../../../assets/school.png';
import {
  Building2,
  Award,
  MapPin,
  Map,
  Bookmark,
  ArrowLeft,
  RefreshCw,
  LayoutGrid,
  Globe,
  Building,
  Wrench,
  ChevronDown,
  Laptop,
  BookOpen,
  Accessibility,
  Shield,
  Leaf,
  Monitor,
  Wifi,
  Camera,
  FlaskConical,
} from 'lucide-react';

// Analytics components
import {
  StatCard,
  ComparisonBarChart,
  DistributionPieChart,
  RegionComparisonTable,
  PhaseDistributionChart,
  ComparisonRadarChart,
  StateWiseTrendChart,
} from '../components/analytics';

// Analytics API and utilities
import {
  getAllSchoolsForAnalytics,
  getStateWiseAnalytics,
  getRegionWiseAnalytics,
  calculateSummaryStats,
  getPmshriPhaseDistribution,
  getLocationTypeDistribution,
  getSponsorshipDistribution,
  getStationTypeDistribution,
  getInternetDistribution,
  getBuildingTypeDistribution,
  getOwnershipTypeDistribution,
  getDetailedSchoolAnalytics,
  calculateInfrastructureComparison,
  calculateFacilityAvailability,
  calculateEnrollmentComparison,
  calculateCWSNComparison,
  calculateGreenBuildingComparison,
  SchoolAnalyticsData,
  SchoolDetailedAnalytics,
} from '../services/analyticsApi';

// Tab configuration
const TABS = [
  { id: 0, label: 'Overview', icon: LayoutGrid },
  { id: 1, label: 'Regional Analysis', icon: Globe },
  { id: 2, label: 'State Analysis', icon: Map },
  // { id: 3, label: 'Infrastructure', icon: Wrench },
  // { id: 4, label: 'ICT & Library', icon: Laptop },
  // { id: 5, label: 'Facilities & Safety', icon: Shield },
];

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const carouselImages = [img1];

  // Data states
  const [allSchools, setAllSchools] = useState<SchoolAnalyticsData[]>([]);
  const [stateWiseData, setStateWiseData] = useState<any[]>([]);
  const [regionWiseData, setRegionWiseData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [schools, states, regions] = await Promise.all([
          getAllSchoolsForAnalytics(),
          getStateWiseAnalytics(),
          getRegionWiseAnalytics(),
        ]);
        setAllSchools(schools);
        setStateWiseData(states);
        setRegionWiseData(regions);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtered schools based on selections
  const filteredSchools = useMemo(() => {
    let schools = allSchools;
    if (selectedState) {
      schools = schools.filter((s) => s.stateName === selectedState);
    }
    if (selectedRegion) {
      schools = schools.filter((s) => s.roName === selectedRegion);
    }
    return schools;
  }, [allSchools, selectedState, selectedRegion]);

  // Calculate statistics
  const stats = useMemo(() => calculateSummaryStats(filteredSchools), [filteredSchools]);

  // Get distributions
  const phaseDistribution = useMemo(() => getPmshriPhaseDistribution(filteredSchools), [filteredSchools]);
  const locationDistribution = useMemo(() => getLocationTypeDistribution(filteredSchools), [filteredSchools]);
  const sponsorshipDistribution = useMemo(() => getSponsorshipDistribution(filteredSchools), [filteredSchools]);
  const stationDistribution = useMemo(() => getStationTypeDistribution(filteredSchools), [filteredSchools]);
  const internetDistribution = useMemo(() => getInternetDistribution(filteredSchools), [filteredSchools]);
  const buildingDistribution = useMemo(() => getBuildingTypeDistribution(filteredSchools), [filteredSchools]);
  const ownershipDistribution = useMemo(() => getOwnershipTypeDistribution(filteredSchools), [filteredSchools]);

  // Filtered state/region data based on selections
  const filteredStateData = useMemo(() => {
    if (selectedState) {
      return stateWiseData.filter((s) => s.stateName === selectedState);
    }
    return stateWiseData;
  }, [stateWiseData, selectedState]);

  const filteredRegionData = useMemo(() => {
    if (selectedRegion) {
      return regionWiseData.filter((r) => r.roName === selectedRegion);
    }
    return regionWiseData;
  }, [regionWiseData, selectedRegion]);

  // Get unique states and regions for filters
  const uniqueStates = useMemo(() => {
    const states = new Set(allSchools.map((s) => s.stateName).filter(Boolean));
    return Array.from(states).sort();
  }, [allSchools]);

  const uniqueRegions = useMemo(() => {
    const regions = new Set(allSchools.map((s) => s.roName).filter(Boolean));
    return Array.from(regions).sort();
  }, [allSchools]);

  // Handlers
  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(event.target.value);
  };

  const handleRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRegion(event.target.value);
  };

  const handleReset = () => {
    setSelectedState('');
    setSelectedRegion('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
        <div className="w-full max-w-7xl mx-auto px-4">
          <Breadcrumb />
        </div>
        <main className="flex-grow flex items-center justify-center">
          <KVSLoader />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl mx-auto px-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive analysis of PM-SHRI and Non PM-SHRI schools
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/schools')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Schools
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* State Filter */}
            <div className="relative min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Filter by State</label>
              <div className="relative">
                <select
                  value={selectedState}
                  onChange={handleStateChange}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All States</option>
                  {uniqueStates.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Region Filter */}
            <div className="relative min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Filter by Region</label>
              <div className="relative">
                <select
                  value={selectedRegion}
                  onChange={handleRegionChange}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Regions</option>
                  {uniqueRegions.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Reset Button */}
            <div className="flex items-end">
              <button
                onClick={handleReset}
                disabled={!selectedState && !selectedRegion}
                className="flex items-center gap-2 px-4 py-2 mt-5 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                Reset Filters
              </button>
            </div>

            {(selectedState || selectedRegion) && (
              <span className="text-sm text-gray-500 ml-auto mt-5">
                Showing: {selectedState || 'All States'} | {selectedRegion || 'All Regions'}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Summary Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <StatCard
            title="Total Schools"
            value={stats.totalSchools}
            icon={<Building2 className="w-6 h-6" />}
            color="purple"
            subtitle="All KVS Schools"
          />
          <StatCard
            title="PM-SHRI Schools"
            value={stats.pmshriCount}
            icon={<Award className="w-6 h-6" />}
            color="green"
            subtitle={`${stats.pmshriPercentage}% of total`}
          />
          <StatCard
            title="Non PM-SHRI"
            value={stats.nonPmshriCount}
            icon={<Building className="w-6 h-6" />}
            color="blue"
            subtitle={`${(100 - parseFloat(stats.pmshriPercentage)).toFixed(1)}% of total`}
          />
          <StatCard
            title="States/UTs"
            value={stats.totalStates}
            icon={<Map className="w-6 h-6" />}
            color="orange"
            subtitle="Coverage across India"
          />
          <StatCard
            title="Regions"
            value={stats.totalRegions}
            icon={<MapPin className="w-6 h-6" />}
            color="pink"
            subtitle="Administrative regions"
          />
          <StatCard
            title="Districts"
            value={stats.totalDistricts}
            icon={<Bookmark className="w-6 h-6" />}
            color="teal"
            subtitle="District coverage"
          />
        </div>

        {/* Tabs Navigation - PM-SHRI Style */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Panels */}
        {activeTab === 0 && (
          /* Overview Tab */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PM-SHRI Phase Distribution */}
            <PhaseDistributionChart
              data={phaseDistribution}
              title="PM-SHRI Phase Distribution"
            />

            {/* Location Type Distribution */}
            <DistributionPieChart
              data={locationDistribution.map((d) => ({
                name: d.category,
                value: d.total,
              }))}
              title="Location Type Distribution"
            />

            {/* PM-SHRI vs Non PM-SHRI by Location */}
            <ComparisonBarChart
              data={locationDistribution}
              title="PM-SHRI vs Non PM-SHRI by Location Type"
              xAxisLabel="Location Type"
            />

            {/* Sponsorship Distribution */}
            <ComparisonRadarChart
              data={sponsorshipDistribution}
              title="PM-SHRI vs Non PM-SHRI by Sponsorship"
            />
          </div>
        )}

        {activeTab === 1 && (
          /* Regional Analysis Tab */
          <div className="space-y-6">
            <RegionComparisonTable
              data={filteredRegionData}
              title="Region-wise PM-SHRI Distribution"
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ComparisonBarChart
                data={filteredRegionData.slice(0, 10).map((r) => ({
                  category: r.roName,
                  pmshriCount: r.pmshriCount,
                  nonPmshriCount: r.nonPmshriCount,
                  total: r.totalSchools,
                  pmshriPercentage: parseFloat(r.pmshriPercentage) || 0,
                }))}
                title="Top 10 Regions by School Count"
                xAxisLabel="Region"
              />

              <DistributionPieChart
                data={filteredRegionData.map((r) => ({
                  name: r.roName,
                  value: r.totalSchools,
                }))}
                title="Schools Distribution by Region"
                height={400}
              />
            </div>
          </div>
        )}

        {activeTab === 2 && (
          /* State Analysis Tab */
          <div className="space-y-6">
            <StateWiseTrendChart
              data={filteredStateData}
              title="State-wise School Distribution"
              sortBy="total"
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ComparisonBarChart
                data={filteredStateData.slice(0, 10).map((s) => ({
                  category: s.stateName,
                  pmshriCount: s.pmshriCount,
                  nonPmshriCount: s.nonPmshriCount,
                  total: s.totalSchools,
                  pmshriPercentage: parseFloat(s.pmshriPercentage) || 0,
                }))}
                title="Top 10 States by PM-SHRI Schools"
                xAxisLabel="State"
              />

              <DistributionPieChart
                data={filteredStateData
                  .sort((a, b) => b.pmshriCount - a.pmshriCount)
                  .slice(0, 10)
                  .map((s) => ({
                    name: s.stateName,
                    value: s.pmshriCount,
                  }))}
                title="PM-SHRI Schools by State (Top 10)"
                height={400}
              />
            </div>
          </div>
        )}

        {/* Infrastructure Tab (commented out) */}

        {/* ICT & Library Tab (commented out) */}

        {/* Facilities & Safety Tab (commented out) */}

        {/* Quick Insights Section */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Quick Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Highest PM-SHRI Concentration</p>
              <p className="text-lg font-semibold text-[#6A1786]">
                {regionWiseData.length > 0
                  ? regionWiseData.reduce((max, r) =>
                      parseFloat(r.pmshriPercentage) > parseFloat(max.pmshriPercentage) ? r : max
                    ).roName
                  : 'N/A'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Most Schools Region</p>
              <p className="text-lg font-semibold text-[#6A1786]">
                {regionWiseData.length > 0
                  ? regionWiseData.reduce((max, r) =>
                      r.totalSchools > max.totalSchools ? r : max
                    ).roName
                  : 'N/A'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Urban Schools</p>
              <p className="text-lg font-semibold text-[#6A1786]">
                {locationDistribution.find((l) => l.category === 'Urban')?.total.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Rural Schools</p>
              <p className="text-lg font-semibold text-[#6A1786]">
                {locationDistribution.find((l) => l.category === 'Rural')?.total.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Analysis Quick Access */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => (
              <button
                key={item.name}
                onClick={() => navigate(item.path)}
                className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center hover:shadow-lg transition-all cursor-pointer hover:scale-105`}
              >
                <span className="text-3xl">{item.icon}</span>
                <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
              </button>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
